import { useState } from "react";
import { useMarketplaceStore } from "../../store/useMarketplaceStore";
import { useCustom, useCustomMutation, useInvalidate } from "@refinedev/core";
import { useTranslation } from "../../hooks/useTranslation";
import {
  Typography,
  Grid2,
  Stack,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Alert,
  Card,
  CardContent,
  Divider,
  Container,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Cancel";
import LoadingButton from "@mui/lab/LoadingButton";
import { SyncrosaleLoader } from "../../components/layout/components/SyncrosaleLoader";

type Carrier = {
  id: number;
  name: string;
  code: string;
  logoUrl: string;
  isActive: boolean;
};

type CarrierService = {
  id: number;
  name: string;
  description?: string;
};

type DefaultServiceResponse = {
  defaultCarrierId: number;
  defaultCarrierServiceId: number;
};

export const ServiceSelection = () => {
  const invalidate = useInvalidate();
  const selectedCountry = useMarketplaceStore((state) => state.selectedCountry);
  const { translate, translateWithoutPrefix } = useTranslation(
    "pages.warehouse.serviceSelection",
  );
  const [selectedCarrierId, setSelectedCarrierId] = useState<number>();
  const [selectedServiceId, setSelectedServiceId] = useState<number>();
  const [isEditing, setIsEditing] = useState(false);

  const { data: carriersResponse, isFetching: carriersLoading } = useCustom<{
    carriers: Carrier[];
  }>({
    url: selectedCountry ? `store/${selectedCountry.id}/carriers` : "",
    method: "get",
    queryOptions: {
      enabled: !!selectedCountry,
    },
  });

  const { data: defaultServiceResponse, isFetching: defaultServiceLoading } =
    useCustom<DefaultServiceResponse>({
      url: selectedCountry
        ? `store/${selectedCountry.id}/carriers/default-carrier-service`
        : "",
      method: "get",
      queryOptions: {
        enabled: !!selectedCountry,
        retry: false,
      },
    });

  const defaultService = defaultServiceResponse?.data;
  const hasDefaultService =
    !!defaultService?.defaultCarrierId &&
    !!defaultService?.defaultCarrierServiceId;

  const { data: servicesResponse, isFetching: servicesLoading } = useCustom<{
    carrierServices: CarrierService[];
  }>({
    url: `store/${selectedCountry?.id}/carriers/${selectedCarrierId ?? defaultService?.defaultCarrierId}/carrier-services`,
    method: "get",
    queryOptions: {
      enabled: !!selectedCountry && (hasDefaultService || !!selectedCarrierId),
    },
  });

  const { mutate: updateDefaultService, isLoading: updating } =
    useCustomMutation();

  const handleCarrierChange = (carrierId: number) => {
    setSelectedCarrierId(carrierId);
    setSelectedServiceId(undefined);
  };

  const handleSaveSelection = () => {
    if (selectedCarrierId && selectedServiceId) {
      updateDefaultService(
        {
          url: `store/${selectedCountry!.id}/carriers/default-carrier-service`,
          method: "put",
          values: {
            defaultCarrierId: selectedCarrierId,
            defaultCarrierServiceId: selectedServiceId,
          },
          successNotification: () => ({
            message: translate("updateSuccess"),
            type: "success",
          }),
        },
        {
          onSuccess: () => {
            setIsEditing(false);
            invalidate({
              resource: "all",
              invalidates: ["all"],
            });
          },
        },
      );
    }
  };

  const handleEdit = () => {
    setIsEditing(true);

    if (hasDefaultService) {
      setDefaultSelected(
        defaultService.defaultCarrierId,
        defaultService.defaultCarrierServiceId,
      );
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
    clearDefaultSelected();
  };

  const setDefaultSelected = (carrierId: number, serviceId: number) => {
    setSelectedCarrierId(carrierId);
    setSelectedServiceId(serviceId);
  };

  const clearDefaultSelected = () => {
    setSelectedCarrierId(undefined);
    setSelectedServiceId(undefined);
  };

  if (carriersLoading || defaultServiceLoading || servicesLoading) {
    return <SyncrosaleLoader />;
  }

  const carriers = carriersResponse?.data?.carriers || [];
  const services = servicesResponse?.data?.carrierServices || [];

  const selectedCarrier = carriers.find(
    (carrier) => carrier.id === defaultService?.defaultCarrierId,
  );

  const selectedService = services.find(
    (service) => service.id === defaultService?.defaultCarrierServiceId,
  );

  return (
    <Container component={Card} sx={{ p: 3 }} maxWidth={false}>
      <Stack spacing={3}>
        <Grid2 container spacing={3}>
          <Grid2 size={{ xs: 12, md: 6 }}>
            <Box>
              <Typography variant="h5" gutterBottom>
                {translate("title")}
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                {translate("description")}
              </Typography>

              <Typography variant="body2" color="text.secondary">
                {translate("serviceSelectionNote")}
              </Typography>
            </Box>
          </Grid2>
          <Grid2 size={{ xs: 12, md: 6 }}>
            {!isEditing ? (
              <Stack spacing={3}>
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Typography variant="h6">
                    {translate("currentSelection")}
                  </Typography>
                  <Button
                    variant="outlined"
                    startIcon={<EditIcon />}
                    onClick={handleEdit}
                  >
                    {translateWithoutPrefix("buttons.edit")}
                  </Button>
                </Stack>

                {hasDefaultService ? (
                  <Card variant="outlined">
                    <CardContent>
                      <Stack spacing={2}>
                        <Stack direction="row" alignItems="center" spacing={2}>
                          <img
                            src={selectedCarrier?.logoUrl}
                            alt={selectedCarrier?.name}
                            style={{
                              width: 32,
                              height: 32,
                              objectFit: "contain",
                            }}
                          />
                          <Box>
                            <Typography variant="h6">
                              {selectedCarrier?.name}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {translate("carrier")}
                            </Typography>
                          </Box>
                        </Stack>

                        <Divider />

                        <Typography variant="subtitle1" gutterBottom>
                          {selectedService?.name}
                        </Typography>
                      </Stack>
                    </CardContent>
                  </Card>
                ) : (
                  <Alert severity="info">{translate("noDefaultService")}</Alert>
                )}
              </Stack>
            ) : (
              <Stack spacing={3}>
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Typography variant="h6">
                    {translate("editSelection")}
                  </Typography>
                  <Stack direction="row" spacing={1}>
                    <Button
                      variant="outlined"
                      startIcon={<CancelIcon />}
                      onClick={handleCancel}
                    >
                      {translateWithoutPrefix("buttons.cancel")}
                    </Button>
                    <LoadingButton
                      loading={updating}
                      variant="contained"
                      startIcon={<SaveIcon />}
                      onClick={handleSaveSelection}
                      disabled={
                        !selectedCarrierId || !selectedServiceId || updating
                      }
                    >
                      {translateWithoutPrefix("buttons.save")}
                    </LoadingButton>
                  </Stack>
                </Stack>

                <FormControl fullWidth>
                  <InputLabel>{translate("selectCarrier")}</InputLabel>
                  <Select
                    value={selectedCarrierId ?? ""}
                    label={translate("selectCarrier")}
                    onChange={(e) =>
                      handleCarrierChange(e.target.value as number)
                    }
                  >
                    {carriers.map((carrier) => (
                      <MenuItem key={carrier.id} value={carrier.id}>
                        <Stack direction="row" alignItems="center" spacing={2}>
                          <img
                            src={carrier.logoUrl}
                            alt={carrier.name}
                            style={{
                              width: 24,
                              height: 24,
                              objectFit: "contain",
                            }}
                          />
                          <Typography>{carrier.name}</Typography>
                        </Stack>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {selectedCarrierId && (
                  <FormControl fullWidth>
                    <InputLabel>{translate("selectService")}</InputLabel>
                    <Select
                      value={selectedServiceId ?? ""}
                      label={translate("selectService")}
                      onChange={(e) =>
                        setSelectedServiceId(e.target.value as number)
                      }
                      disabled={servicesLoading}
                    >
                      {services.map((service) => (
                        <MenuItem key={service.id} value={service.id}>
                          {service.name}
                          {service.description && ` - ${service.description}`}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
              </Stack>
            )}
          </Grid2>
        </Grid2>
      </Stack>
    </Container>
  );
};
